import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useGetSessionActivityQuery } from '../../../app/redux-fetch/apiImpersonation'
import { ImpersonationSessionActivity } from './types'
import messages from './messages'

dayjs.extend(relativeTime)

// E11 R5 (WI 616) — the impersonation banner's "View session activity" drawer. A right-anchored panel that,
// while open, lists the recent audited writes attributed to the active session (GET
// /v1/impersonation/sessions/{id}/activity), most-recent-first with a relative timestamp. The scope note
// documents that only User-Management writes are shown (cross-DB writes are out of v19 scope). The list does
// NOT auto-poll — the query fires on open (and re-fires via the Refresh button); a 30s interval only re-renders
// so the relative timestamps age ("5 minutes ago" -> "6 minutes ago") without any network call.

// Mirrors the server's default @Top; when the API returns exactly this many rows the footer warns the view
// is capped (older activity is in the full audit dashboard).
export const ACTIVITY_PAGE_SIZE = 50

interface SessionActivityDrawerProps {
  sessionId: number
  open: boolean
  onClose: () => void
}

// "Sarah Hassan #17" for named entities; "#482" for the label-less allocation/detail tables (where the server
// sends the id as the DisplayLabel) — the entity Chip already names the type, so the bare id reads cleanly.
const rowLabel = (row: ImpersonationSessionActivity): string =>
  row.displayLabel === String(row.entityId) ? `#${row.entityId}` : `${row.displayLabel} #${row.entityId}`

const SessionActivityDrawer = ({ sessionId, open, onClose }: SessionActivityDrawerProps) => {
  const { formatMessage } = useIntl()
  const { data, isFetching, isError, refetch } = useGetSessionActivityQuery(
    { sessionId, top: ACTIVITY_PAGE_SIZE },
    // Fetch only while open; re-fetch on every (re)open so a returning admin sees current activity.
    { skip: !open, refetchOnMountOrArgChange: true },
  )

  // Re-render every 30s while open so the relative timestamps age. No API calls (Refresh is the only refetch).
  const [, setNowTick] = useState(0)
  useEffect(() => {
    if (!open) return undefined
    const id = window.setInterval(() => setNowTick((t) => t + 1), 30_000)
    return () => window.clearInterval(id)
  }, [open])

  const rows = data ?? []
  const atCap = rows.length >= ACTIVITY_PAGE_SIZE

  const body = () => {
    if (isFetching && rows.length === 0) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
          <CircularProgress size={16} />
          <Typography variant='body2'>{formatMessage(messages.activityLoading)}</Typography>
        </Box>
      )
    }
    if (isError) {
      return (
        <Typography variant='body2' color='error' sx={{ p: 2 }}>
          {formatMessage(messages.activityError)}
        </Typography>
      )
    }
    if (rows.length === 0) {
      return (
        <Typography variant='body2' color='text.secondary' sx={{ p: 2 }}>
          {formatMessage(messages.activityEmpty)}
        </Typography>
      )
    }
    return (
      <List dense disablePadding>
        {rows.map((row, i) => (
          <ListItem key={`${row.entity}-${row.entityId}-${row.timestamp}-${i}`} divider>
            <ListItemText
              disableTypography
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip size='small' label={row.entity} />
                  <Typography variant='body2' sx={{ fontWeight: 500 }}>
                    {rowLabel(row)}
                  </Typography>
                </Box>
              }
              secondary={
                <Typography variant='caption' color='text.secondary'>
                  {dayjs(row.timestamp).fromNow()}
                </Typography>
              }
            />
          </ListItem>
        ))}
      </List>
    )
  }

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      aria-label='session-activity-drawer'
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant='h6' sx={{ flexGrow: 1 }}>
          {formatMessage(messages.activityTitle)}
        </Typography>
        <Tooltip title={formatMessage(messages.activityRefresh)}>
          {/* span so the tooltip still shows while the button is disabled (fetching). */}
          <span>
            <IconButton
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label={formatMessage(messages.activityRefresh)}
            >
              <RefreshRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton onClick={onClose} aria-label={formatMessage(messages.activityClose)}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>
      <Typography variant='caption' color='text.secondary' sx={{ px: 2, pb: 1, display: 'block' }}>
        {formatMessage(messages.activityScopeNote)}
      </Typography>
      <Divider />
      {body()}
      {atCap && (
        <Typography variant='caption' color='text.secondary' sx={{ p: 2, display: 'block' }}>
          {formatMessage(messages.activityCapWarning, { count: ACTIVITY_PAGE_SIZE })}
        </Typography>
      )}
    </Drawer>
  )
}

export default SessionActivityDrawer
